import { useState } from "react";
import { DataTable } from "@/components/data-tables/data-table";
import { createAuthProviderColumns, AuthProviderSearchableColumns } from "./AuthProviderDataTableColumns";
import { useDataTable } from "@/hooks/useDataTable";
import { Button } from "@/components/ui/button";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import {
    useDeleteAuthProviderMutation,
    useSetEnabledAuthProviderMutation,
} from "@/api/mutations/admin/AuthProviderMutations";
import { toast } from "sonner";
import { AuthProviderDialog } from "@/components/dialogs/providers/AuthProviderDialog";
import { Provider } from "@shared/validators/admin/settings-validators";
import { useTranslation } from "react-i18next";

interface AuthProviderActions {
    onViewDetails?: (provider: Provider) => void;
    onEdit?: (provider: Provider) => void;
    onToggleEnabled?: (provider: Provider) => void;
}

type AuthProviderDataTableProps = {
    data: Provider[];
    actions?: AuthProviderActions;
};

const AuthProviderDataTable = ({ data, actions }: AuthProviderDataTableProps) => {
    const { t } = useTranslation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);

    const setEnabledAuthProviderMutation = useSetEnabledAuthProviderMutation();

    // Default action callbacks
    const defaultActions: AuthProviderActions = {
        onViewDetails: (provider) => {
            toast.info(t("dataTables.viewingDetailsFor", { name: provider.display_name }));
            // TODO: Implement view details modal
        },
        onEdit: (provider) => {
            setSelectedProvider(provider);
            setEditDialogOpen(true);
        },
        onToggleEnabled: (provider) => {
            setEnabledAuthProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
    };

    const finalActions = actions || defaultActions;
    const columns = createAuthProviderColumns(finalActions);

    const { table } = useDataTable({
        columns,
        data,
        pageCount: 1,
        searchableColumns: AuthProviderSearchableColumns,
    });

    const rows = table.getSelectedRowModel();
    const ids = rows.flatRows.map((row) => row.original.id) as string[];
    const { mutate } = useDeleteAuthProviderMutation();

    const handleDeleteProviders = () => {
        if (confirm(t("dataTables.confirmDeleteAuthProviders"))) {
            ids.forEach((id) => mutate({ id }));
        }
    };

    const handleCreateProvider = () => {
        setCreateDialogOpen(true);
    };

    return (
        <>
            <DataTable
                table={table}
                columns={columns}
                searchableColumns={AuthProviderSearchableColumns}
                newRowButton={
                    <Button variant="outline" size="sm" className="h-8" onClick={handleCreateProvider}>
                        <PlusCircledIcon className="mr-2 size-4" aria-hidden="true" />
                        {t("dataTables.newAuthProvider")}
                    </Button>
                }
                deleteRowsAction={handleDeleteProviders}
            />

            {/* Create Provider Dialog */}
            <AuthProviderDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} mode="create" />

            {/* Edit Provider Dialog */}
            <AuthProviderDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                provider={selectedProvider}
                mode="edit"
            />
        </>
    );
};

export default AuthProviderDataTable;
